import React, { useState, useEffect, ChangeEvent } from "react"
import { graphql, navigate } from "gatsby"
import { getImage } from "gatsby-plugin-image"

import SearchInput from "../components/SearchInput"
import ProjectCard from "../components/ProjectCard"
import ProjectSelector from "../components/ProjectSelector"

const SoftwarePage = ({ data, title = "Software" }) => {
  const allProjects = data.allStothartProjectsJson.nodes
  const [projectSearchInput, setProjectSearchInput] = useState("")
  const [selectedProjectData, setSelectedProjectData] = useState(allProjects)
  const [selectedProjectCount, setSelectedProjectCount] = useState(
    allProjects.length
  )

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setProjectSearchInput(event.target.value)
  }

  useEffect(() => {
    if (typeof window !== "undefined") {
      if (window.location.search) {
        try {
          let query = window.location.search.split("=")[1]
          query = decodeURI(query)
          setProjectSearchInput(query)
        } catch {}
      }
    }
  }, [])

  useEffect(() => {
    const projectSearchInputArray = projectSearchInput
      .toLowerCase()
      .trim()
      .split(" ")

    const newSelectedProjectData = data.allStothartProjectsJson.nodes.filter(
      project => {
        let inputWordsFound = 0
        for (const inputWord of projectSearchInputArray) {
          let inputWordFound = false
          for (const property in project) {
            if (project[property] && property !== "url") {
              const propertyValue = project[property].toString().toLowerCase()
              if (propertyValue.includes(inputWord)) {
                inputWordFound = true
              }
            }
          }
          if (inputWordFound) inputWordsFound += 1
        }
        return inputWordsFound === projectSearchInputArray.length ? true : false
      }
    )

    setSelectedProjectData(newSelectedProjectData)
    setSelectedProjectCount(newSelectedProjectData.length)

    if (projectSearchInput.trim().length > 0) {
      navigate(`?q=${projectSearchInput.trim()}`)
    } else {
      navigate("/software")
    }
  }, [projectSearchInput])

  const projectCards = selectedProjectData.map(project => {
    const image = getImage(project.image)

    return (
      <ProjectCard
        key={project.id}
        title={project.title}
        description={project.description}
        image={image}
        technologies={project.technologies}
        url={project.url ? project.url : null}
        year={project.year}
      />
    )
  })

  return (
    <>
      <div className="page-intro">
        <div className="page-intro__title-row">
          <h1>Software</h1>
          <span aria-label={`${selectedProjectCount} software projects`}>
            {selectedProjectCount}
          </span>
        </div>
      </div>
      <div className="listing-search">
        <SearchInput
          label="Search Software"
          handleInputChange={handleInputChange}
          searchInput={projectSearchInput}
          searchResultCount={selectedProjectCount}
        />
      </div>

      <div
        className="filter-list"
        role="group"
        aria-label="Filter software by technology"
      >
        <button
          type="button"
          className={`filter-chip ${
            projectSearchInput.trim() ? "" : "is-active"
          }`}
          aria-pressed={!projectSearchInput.trim()}
          onClick={() => {
            setProjectSearchInput("")
            navigate("/software")
          }}
        >
          all
        </button>
        {data.allTechnologiesJson.nodes.map(technology => {
          const isOn = projectSearchInput
            .toLowerCase()
            .includes(technology.name.toLowerCase())
            ? true
            : false

          return (
            <ProjectSelector
              key={technology.name}
              selectorString={technology.name}
              on={isOn}
              onClick={() => {
                if (!isOn) {
                  setProjectSearchInput(oldInput =>
                    `${oldInput.trim()} ${technology.name.toLowerCase()}`.trim()
                  )
                } else {
                  setProjectSearchInput(oldInput => {
                    let newInput = oldInput
                      .toLowerCase()
                      .replaceAll(technology.name.toLowerCase(), "")
                      .replace(/\s{2,}/g, " ")
                      .trim()
                    return newInput
                  })
                }
              }}
            />
          )
        })}
      </div>

      <div
        key={projectSearchInput.trim().toLowerCase() || "all-software"}
        className="filter-results project-grid"
      >
        {selectedProjectCount > 0 ? (
          projectCards
        ) : (
          <p className="empty-results" role="status">
            No software found.
          </p>
        )}
      </div>
    </>
  )
}

export const query = graphql`
  {
    allStothartProjectsJson(sort: { fields: year, order: DESC }) {
      nodes {
        id
        title
        description
        url
        technologies
        year
        image {
          childImageSharp {
            gatsbyImageData(placeholder: BLURRED)
          }
        }
      }
    }
    allTechnologiesJson {
      nodes {
        name
      }
    }
  }
`

export default SoftwarePage
